import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from "@mui/material";
import { useAuth } from "@bw/shared/hooks";
import { useState } from "react";

import { useBlockPlayer } from "../hooks/useBlockPlayer";

export interface BlockPlayerButtonProps {
  playerId: string;
  playerName: string;
  onBlocked?: () => void;
}

function safeErrorMessage(
  error: unknown,
  fallback: string,
  hiddenIdentity: string,
): string {
  if (!(error instanceof Error) || !error.message.trim()) return fallback;
  return error.message.includes(hiddenIdentity) ? fallback : error.message;
}

/** Profile-scoped block control backed only by server relationship truth. */
export function BlockPlayerButton({
  playerId,
  playerName,
  onBlocked,
}: BlockPlayerButtonProps) {
  const { user } = useAuth();
  const isSelf = Boolean(user?.uid) && user?.uid === playerId;
  const { status, change } = useBlockPlayer(playerId, !isSelf);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const visibleName =
    playerName.trim() && playerName !== playerId ? playerName : "this player";

  if (isSelf) return null;

  if (status.isLoading) {
    return (
      <Button
        aria-label={`Checking block status for ${visibleName}`}
        disabled
        size="small"
        variant="outlined"
      >
        Checking…
      </Button>
    );
  }

  if (status.isError || !status.data) {
    return (
      <Alert
        action={
          <Button color="inherit" onClick={() => status.refetch()} size="small">
            Try again
          </Button>
        }
        role="alert"
        severity="error"
      >
        {safeErrorMessage(
          status.error,
          "Block status is unavailable.",
          playerId,
        )}
      </Alert>
    );
  }

  const isBlocked = status.data.is_blocked;
  const mutationError = change.isError
    ? safeErrorMessage(
        change.error,
        isBlocked
          ? "Could not unblock this player."
          : "Could not block this player.",
        playerId,
      )
    : null;

  if (isBlocked) {
    return (
      <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
        <Button
          aria-label={`${change.isPending ? "Unblocking" : "Unblock"} ${visibleName}`}
          disabled={change.isPending}
          onClick={() => change.mutate(false)}
          size="small"
          variant="outlined"
        >
          {change.isPending ? "Unblocking…" : "Unblock"}
        </Button>
        {mutationError ? (
          <Alert role="alert" severity="error">
            {mutationError}
          </Alert>
        ) : null}
      </Stack>
    );
  }

  return (
    <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
      <Button
        aria-label={`Block ${visibleName}`}
        disabled={change.isPending}
        onClick={() => {
          change.reset();
          setConfirmOpen(true);
        }}
        size="small"
        variant="outlined"
      >
        Block
      </Button>
      <Dialog
        aria-describedby="block-player-description"
        aria-labelledby="block-player-title"
        open={confirmOpen}
        onClose={() => {
          if (!change.isPending) setConfirmOpen(false);
        }}
      >
        <DialogTitle id="block-player-title">Block {visibleName}?</DialogTitle>
        <DialogContent>
          <DialogContentText id="block-player-description">
            You will no longer see each other&apos;s social activity or be able
            to follow or start new conversations. You can undo this from Blocked
            players.
          </DialogContentText>
          {mutationError ? (
            <Alert role="alert" severity="error" sx={{ mt: 2 }}>
              {mutationError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            disabled={change.isPending}
            onClick={() => setConfirmOpen(false)}
            size="small"
          >
            Cancel
          </Button>
          <Button
            color="error"
            disabled={change.isPending}
            onClick={() =>
              change.mutate(true, {
                onSuccess: () => {
                  setConfirmOpen(false);
                  onBlocked?.();
                },
              })
            }
            size="small"
            variant="contained"
          >
            {change.isPending ? "Blocking…" : "Block"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
